import * as React from 'react';

export type Feature =
  | 'all'
  | 'administrative'
  | 'administrative.country'
  | 'administrative.land_parcel'
  | 'administrative.locality'
  | 'administrative.neighborhood'
  | 'administrative.province'
  | 'landscape'
  | 'landscape.man_made'
  | 'landscape.natural'
  | 'landscape.natural'
  | 'landscape.natural'
  | 'poi'
  | 'poi.attraction'
  | 'poi.business'
  | 'poi.government'
  | 'poi.medical'
  | 'poi.park'
  | 'poi.place_of_worship'
  | 'poi.school'
  | 'poi.sports_complex'
  | 'road'
  | 'road.arterial'
  | 'road.highway'
  | 'road.highway'
  | 'road.local'
  | 'transit'
  | 'transit.line'
  | 'transit.station'
  | 'transit.station'
  | 'transit.station'
  | 'transit.station'
  | 'water';

export type Element =
  | 'all'
  | 'geometry'
  | 'geometry.fill'
  | 'geometry.stroke'
  | 'labels'
  | 'labels.icon'
  | 'labels.text'
  | 'labels.text.fill'
  | 'labels.text.stroke';

export type Location = string | [number, number];

export type Format =
  | 'png'
  | 'png8'
  | 'png32'
  | 'gif '
  | 'jpg '
  | 'jpg-baseline';

export type MapType = 'roadmap' | 'satellite' | 'hybrid' | 'terrain';

export interface MarkerStyle {
  size?: 'tiny' | 'mid' | 'small';
  color?:
    | string /* 0x000000 24bit */
    | 'black'
    | 'brown'
    | 'green'
    | 'purple'
    | 'yellow'
    | 'blue'
    | 'gray'
    | 'orange'
    | 'red'
    | 'white';
  label?: string | number; // [A-Z0-9] size must be defalt or mid
}

export interface Props {
  center: Location;
  width: number;
  height: number;

  apiKey: string;

  // Optional
  zoom?: number;
  features?: Feature[];
  elements?: Element[];
  format?: Format;
  rules?: string[];
  mapType?: MapType;
  scale?: 1 | 2 | 4;

  // Only support one marker for now
  markers?: Location[];

  stretch: boolean;
}

interface State {}

const SizeAttribute = (p: Props): string => {
  return 'size=' + p.width + 'x' + p.height;
};

const ApiKeyAttribute = (p: Props): string => {
  return 'key=' + p.apiKey;
};

const ZoomAttribute = (p: Props): string | null => {
  return p.zoom ? 'zoom=' + p.zoom : null;
};

const LocationTag = (key: string) => (loc: Location) => {
  if (typeof loc === 'string') {
    return key + '=' + loc;
  } else {
    const [lat, lng] = loc;
    return key + '=' + lat + ',' + lng;
  }
};

const CenterAttribute = (p: Props): string => {
  return LocationTag('center')(p.center);
};

const ScaleAttribute = (p: Props): string | null => {
  return p.scale ? 'scale=' + p.scale : null;
};

const MarkerAttribute = (p: Props): string | null => {
  if (!p.markers) {
    return null;
  }
  const markers = p.markers.map(m => {
    return '' + m[0] + ',' + m[1];
  });
  return 'markers=size:small|color:orange|' + markers.join('|');
};

class GoogleMap extends React.Component<Props, State> {
  public readonly root: string =
    'https://maps.googleapis.com/maps/api/staticmap';

  public render(): JSX.Element {
    const { width, height, stretch } = this.props;

    const attributes: Array<(p: Props) => string | null> = [
      SizeAttribute,
      ZoomAttribute,
      CenterAttribute,
      MarkerAttribute,
      ScaleAttribute,
      ApiKeyAttribute
    ];

    const callWithProps = (f: (p: Props) => string | null) => f(this.props);
    const specified = attributes
      .map(callWithProps)
      .filter(Boolean)
      .concat('style=feature:all|visibility:simplified');

    const query = specified.join('&');

    const src = this.root + '?' + query;

    let style: React.CSSProperties = {
      width: width + 'px',
      height: height + 'px'
    };

    if (stretch) {
      style = {
        maxWidth: '100%',
        height: 'auto',
        width: 'auto'
      };
    }

    return (
      <img
        style={style}
        className="shadow"
        src={src}
        width={width}
        height={height}
        alt=""
      />
    );
  }
}

export default GoogleMap;
